// /Frontend/hoppla-admin/src/features/auth/auth-context.tsx

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  clearStoredAdminSession,
  readStoredAdminSession,
  writeStoredAdminSession,
  type StoredAdminSession,
} from "./auth-storage";
import { ApiError, apiGet, apiPost } from "../../lib/api/http";
import { parseAdminMfaChallenge, type AdminMfaChallenge } from './admin-auth-contract';
export type { AdminMfaChallenge } from './admin-auth-contract';

interface LoginPayload {
  username: string;
  password: string;
  turnstileToken: string;
}

interface AuthContextValue {
  session: StoredAdminSession | null;
  admin: StoredAdminSession["admin"] | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (payload: LoginPayload) => Promise<AdminMfaChallenge | null>;
  verifyMfa: (payload: { challengeId: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<StoredAdminSession | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const LAST_ADMIN_DISCONNECT_AT_KEY = "hoppla-admin:last-disconnect-at";

function writeLastAdminDisconnectAt(adminId: string | undefined | null): void {
  if (!adminId || typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    `${LAST_ADMIN_DISCONNECT_AT_KEY}:${adminId}`,
    String(Date.now()),
  );
}

function normalizeAuthResponse(
  raw: Record<string, unknown>,
  fallback?: StoredAdminSession | null,
): StoredAdminSession {
  const admin = (raw.admin ?? fallback?.admin) as
    | StoredAdminSession["admin"]
    | undefined;

  const sessionId =
    (raw.sessionId as string | undefined) ??
    (raw.session as { id?: string } | undefined)?.id ??
    fallback?.sessionId ??
    null;

  if (!admin?.id) {
    throw new Error("Réponse d'authentification invalide.");
  }

  return {
    admin,
    sessionId,
  };
}

async function postJson(
  path: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  try {
    return await apiPost<Record<string, unknown>>(path, body, {
      auth: false,
      retryOnUnauthorized: false,
    });
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 0) {
        throw new Error("Connexion à l’API admin impossible. Vérifiez l’accès Cloudflare à Bulle et à l’API, puis rechargez la page.");
      }
      throw new Error(
        error.message || "La requête d'authentification a échoué.",
      );
    }

    throw error;
  }
}

async function getJson(path: string): Promise<Record<string, unknown>> {
  try {
    return await apiGet<Record<string, unknown>>(path, {
      auth: false,
      retryOnUnauthorized: false,
    });
  } catch (error) {
    if (error instanceof ApiError) {
      throw new Error(error.message || "La vérification de session a échoué.");
    }

    throw error;
  }
}

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [session, setSession] = useState<StoredAdminSession | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const refresh = useCallback(async (): Promise<StoredAdminSession | null> => {
    const current = readStoredAdminSession();

    if (!current?.sessionId) {
      clearStoredAdminSession();
      setSession(null);
      return null;
    }

    try {
      const raw = await postJson("/admin/auth/refresh", {});

      const normalized = normalizeAuthResponse(raw, current);
      writeStoredAdminSession(normalized);
      setSession(normalized);
      return normalized;
    } catch {
      clearStoredAdminSession();
      setSession(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      const stored = readStoredAdminSession();

      if (!stored) {
        if (!cancelled) {
          setSession(null);
          setIsBootstrapping(false);
        }
        return;
      }

      try {
        let candidate: StoredAdminSession | null = stored;
        if (!candidate?.sessionId) {
          clearStoredAdminSession();

          if (!cancelled) {
            setSession(null);
            setIsBootstrapping(false);
          }
          return;
        }

        try {
          await getJson("/admin/auth/me");
        } catch {
          candidate = await refresh();
          if (!candidate?.sessionId) {
            throw new Error("Session admin invalide.");
          }
          await getJson("/admin/auth/me");
        }

        if (!cancelled) {
          setSession(candidate);
          setIsBootstrapping(false);
        }
      } catch {
        clearStoredAdminSession();

        if (!cancelled) {
          setSession(null);
          setIsBootstrapping(false);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const establishSession = useCallback(
    async (raw: Record<string, unknown>): Promise<void> => {
      const normalized = normalizeAuthResponse(raw, null);

      await getJson("/admin/auth/me");

      writeStoredAdminSession(normalized);
      setSession(normalized);
    },
    [],
  );

  const login = useCallback(
    async ({
      username,
      password,
      turnstileToken,
    }: LoginPayload): Promise<AdminMfaChallenge | null> => {
      const raw = await postJson("/admin/auth/login", {
        username,
        password,
        turnstileToken,
      });

      const challenge = parseAdminMfaChallenge(raw);
      if (challenge) {
        clearStoredAdminSession();
        setSession(null);
        return challenge;
      }

      await establishSession(raw);
      return null;
    },
    [establishSession],
  );

  const verifyMfa = useCallback(
    async (payload: { challengeId: string; code: string }): Promise<void> => {
      const raw = await postJson("/admin/auth/mfa/verify", payload);
      await establishSession(raw);
    },
    [establishSession],
  );

  const logout = useCallback(async (): Promise<void> => {
    const current = readStoredAdminSession();

    try {
      if (current?.sessionId) {
        await postJson("/admin/auth/logout", {
          sessionId: current.sessionId ?? undefined,
        });
      }
    } catch {
      // ignore transport errors
    } finally {
      writeLastAdminDisconnectAt(current?.admin?.id);
      clearStoredAdminSession();
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      admin: session?.admin ?? null,
      isAuthenticated: Boolean(session?.admin?.id),
      isBootstrapping,
      login,
      verifyMfa,
      logout,
      refresh,
    }),
    [session, isBootstrapping, login, verifyMfa, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAdminAuth doit être utilisé dans l'AuthProvider.");
  }

  return value;
}

export const useAuth = useAdminAuth;
